import { and, eq } from "drizzle-orm";
import type { DbSchemaSnapshot, DbSchemaTable } from "./db-schema-snapshot";
import {
  table_relationships,
  table_schema_revisions,
  tables,
} from "./schema";
import { generateULID } from "./snowflake";
import type { PrimaryDB } from "./index";

export type ActiveTableRelationship = {
  childTableId: string;
  childTableName: string;
  parentTableId: string;
  parentTableName: string;
  childColumn: string;
  parentColumn: string;
};

async function checksumTable(table: DbSchemaTable): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(table));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

export async function syncProjectTableSchemas(
  db: PrimaryDB,
  projectId: number,
  snapshot: DbSchemaSnapshot,
): Promise<void> {
  const tableRows = await db
    .select({
      id: tables.id,
      name: tables.name,
      latestRevision: tables.latest_schema_revision,
    })
    .from(tables)
    .where(eq(tables.projects_id, projectId));
  const tableById = new Map(tableRows.map((table) => [table.id, table]));
  const tableByName = new Map(tableRows.map((table) => [table.name, table]));

  for (const schema of Object.values(snapshot.tables)) {
    const table = tableById.get(schema.tableId) ?? tableByName.get(schema.name);
    if (!table) continue;
    const checksum = await checksumTable(schema);
    const [existing] = await db
      .select({ id: table_schema_revisions.id, revision: table_schema_revisions.revision })
      .from(table_schema_revisions)
      .where(and(
        eq(table_schema_revisions.table_id, table.id),
        eq(table_schema_revisions.checksum, checksum),
      ))
      .limit(1);
    const revisionId = existing?.id ?? generateULID();
    const revision = existing?.revision ?? Number(table.latestRevision ?? 0) + 1;
    if (!existing) {
      await db.insert(table_schema_revisions).values({
        id: revisionId,
        project_id: projectId,
        table_id: table.id,
        revision,
        checksum,
        schema,
        createdAt: new Date(),
      });
    }
    await db.update(tables).set({
      active_schema_revision_id: revisionId,
      latest_schema_revision: Math.max(Number(table.latestRevision ?? 0), revision),
    }).where(eq(tables.id, table.id));
  }

  await db.update(table_relationships).set({
    active: false,
    updatedAt: new Date(),
  }).where(eq(table_relationships.project_id, projectId));

  for (const schema of Object.values(snapshot.tables)) {
    const child = tableById.get(schema.tableId) ?? tableByName.get(schema.name);
    if (!child) continue;
    for (const column of Object.values(schema.columns)) {
      if (!column.referencedTable) continue;
      const parent = tableByName.get(column.referencedTable);
      if (!parent) continue;
      await db.insert(table_relationships).values({
        id: generateULID(),
        project_id: projectId,
        child_table_id: child.id,
        parent_table_id: parent.id,
        child_column: column.name,
        parent_column: column.referencedColumn ?? "id",
        relationship_type: "many_to_one",
        active: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      }).onConflictDoUpdate({
        target: [
          table_relationships.child_table_id,
          table_relationships.child_column,
          table_relationships.parent_table_id,
          table_relationships.parent_column,
        ],
        set: { active: true, updatedAt: new Date() },
      });
    }
  }
}

export async function getActiveTableSchema(
  db: PrimaryDB,
  projectId: number,
  tableName: string,
): Promise<DbSchemaTable | null> {
  const [row] = await db.select({ schema: table_schema_revisions.schema })
    .from(tables)
    .leftJoin(
      table_schema_revisions,
      eq(table_schema_revisions.id, tables.active_schema_revision_id),
    )
    .where(and(eq(tables.projects_id, projectId), eq(tables.name, tableName)))
    .limit(1);
  return (row?.schema as DbSchemaTable | null | undefined) ?? null;
}

export async function listActiveTableRelationships(
  db: PrimaryDB,
  projectId: number,
): Promise<ActiveTableRelationship[]> {
  const rows = await db.select({
    childTableId: table_relationships.child_table_id,
    parentTableId: table_relationships.parent_table_id,
    childColumn: table_relationships.child_column,
    parentColumn: table_relationships.parent_column,
  }).from(table_relationships).where(and(
    eq(table_relationships.project_id, projectId),
    eq(table_relationships.active, true),
  ));
  const names = await db.select({ id: tables.id, name: tables.name })
    .from(tables).where(eq(tables.projects_id, projectId));
  const nameById = new Map(names.map((table) => [table.id, table.name]));
  return rows.flatMap((row) => {
    const childTableName = nameById.get(row.childTableId);
    const parentTableName = nameById.get(row.parentTableId);
    return childTableName && parentTableName ? [{
      ...row,
      childTableName,
      parentTableName,
    }] : [];
  });
}
